import { useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useGmailCallback } from '../../react-query/email-account-queries';
import { Box, Typography, CircularProgress, Alert, Button } from '@mui/material';
import MainLayout from '@/layouts/MainLayout';

export default function GmailCallbackPage() {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const error = searchParams.get('error');
    
    // Use ref to prevent double execution in React.StrictMode
    const processed = useRef(false);

    const { mutate, isPending, isError, error: mutationError } = useGmailCallback();

    useEffect(() => {
        if (processed.current) return;

        if (error) {
            console.error("Gmail OAuth Error:", error);
            return;
        }

        if (code && state) {
            processed.current = true;
            mutate({ code, state }, {
                onSuccess: () => {
                    // Navigate back to where the user came from, or dashboard default.
                    // For now, go back to Dashboard/Outreach to continue work.
                    navigate('/dashboard/settings', { replace: true });
                },
                onError: (err) => {
                    console.error("Callback failed:", err);
                }
            });
        }
    }, [code, state, error, mutate, navigate]);

    return (
        <MainLayout>
            <Box sx={{ 
                display: 'flex', 
                flexDirection: 'column', 
                alignItems: 'center', 
                justifyContent: 'center', 
                height: '80vh',
                textAlign: 'center',
                p: 3
            }}>
                {isPending && (
                    <>
                        <CircularProgress size={48} sx={{ mb: 3 }} />
                        <Typography variant="h5" gutterBottom>Connecting Gmail Account...</Typography>
                        <Typography color="text.secondary">Please wait while we verify your credentials.</Typography>
                    </>
                )}

                {(error || isError) && (
                    <>
                        <Alert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
                            Failed to connect Gmail account. 
                            {mutationError ? (mutationError as any).response?.data?.message || mutationError.message : error}
                        </Alert>
                        <Button variant="contained" onClick={() => navigate('/dashboard/outreach')}>
                            Return to Dashboard
                        </Button>
                    </>
                )}

                {!code && !state && !error && !isPending && !isError && (
                    <Typography color="error">Invalid callback URL. Missing code or state.</Typography>
                )}
            </Box>
        </MainLayout>
    );
}
